import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Briefcase, Mail } from "lucide-react";
import { BareLayout } from "../components/Layout";
import { Btn, Card, Field, Input, Select, ErrorSummary, cx } from "../components/kit";
import { api, parseError } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Onboarding() {
  const { t } = useTranslation();
  const { setMe } = useAuth();
  const navigate = useNavigate();
  const [choice, setChoice] = useState(null);
  const [ws, setWs] = useState({ name: "", type: "private_practice", country: "" });
  const [link, setLink] = useState("");
  const [error, setError] = useState(null);
  const fe = (k) => { const x = (error?.errors || []).find((e) => e.field === k); return x && t(`err.${x.code}`); };

  const createWs = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/auth/onboarding", { choice: "practice", workspace: ws });
      setMe(data);
      navigate("/profile?welcome=1");
    } catch (er) {
      setError(parseError(er));
    }
  };
  const openInvite = async (e) => {
    e.preventDefault();
    const token = link.trim().split("/invite/").pop().split(/[?#]/)[0];
    if (!token) return setError({ errors: [{ field: "link", code: "required" }] });
    const { data } = await api.post("/auth/onboarding", { choice: "owner" });
    setMe(data);
    navigate(`/invite/${token}`);
  };

  const Option = ({ id, icon: Icon, title, sub }) => (
    <button data-testid={`role-choice-${id}`} onClick={() => { setChoice(id); setError(null); }} aria-pressed={choice === id}
      className={cx("lift flex w-full items-start gap-4 rounded-2xl border bg-white p-5 text-left", choice === id && "border-primary ring-2 ring-clay/[0.25]")}>
      <span className="rounded-xl bg-peach p-3"><Icon className="h-6 w-6 text-clay-strong" /></span>
      <span><span className="block font-display text-lg font-bold">{title}</span><span className="text-sm text-muted-foreground">{sub}</span></span>
    </button>
  );

  return (
    <BareLayout>
      <div className="mx-auto max-w-2xl py-8">
        <h1 className="text-3xl sm:text-4xl font-extrabold fade-up">{t("onboarding.title")}</h1>
        <p className="mt-2 text-muted-foreground fade-up-2">{t("onboarding.sub")}</p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 fade-up-3">
          <Option id="practice" icon={Briefcase} title={t("onboarding.practice")} sub={t("onboarding.practice_sub")} />
          <Option id="owner" icon={Mail} title={t("onboarding.owner")} sub={t("onboarding.owner_sub")} />
        </div>
        {choice === "practice" && (
          <Card className="mt-6 fade-up">
            <form onSubmit={createWs} className="space-y-4" noValidate>
              <h2 className="text-lg font-bold">{t("onboarding.ws_title")}</h2>
              <ErrorSummary errors={error?.errors} />
              <Field label={t("field.workspace_name")} htmlFor="wsn" error={fe("name")} required><Input id="wsn" data-testid="workspace-name-input" value={ws.name} onChange={(e) => setWs({ ...ws, name: e.target.value })} /></Field>
              <Field label={t("field.workspace_type")} htmlFor="wst">
                <Select id="wst" data-testid="workspace-type-select" value={ws.type} onChange={(e) => setWs({ ...ws, type: e.target.value })}>
                  <option value="private_practice">{t("wstype.private_practice")}</option>
                  <option value="organisation">{t("wstype.organisation")}</option>
                </Select>
              </Field>
              <Field label={t("field.country")} htmlFor="wsc" error={fe("country")} required><Input id="wsc" data-testid="workspace-country-input" value={ws.country} onChange={(e) => setWs({ ...ws, country: e.target.value })} /></Field>
              <Btn type="submit" testid="workspace-create-btn">{t("onboarding.create_ws")}</Btn>
            </form>
          </Card>
        )}
        {choice === "owner" && (
          <Card className="mt-6 fade-up">
            <form onSubmit={openInvite} className="space-y-4">
              <Field label={t("onboarding.invite_link")} htmlFor="inv" error={fe("link")} hint={t("onboarding.invite_hint")}><Input id="inv" data-testid="invite-link-input" value={link} onChange={(e) => setLink(e.target.value)} /></Field>
              <Btn type="submit" testid="invite-open-btn">{t("onboarding.open_invite")}</Btn>
            </form>
          </Card>
        )}
      </div>
    </BareLayout>
  );
}
